// WP-3.3 (M3 PJ-1..7, S-23/24): project queries/mutations. WP-3.6 (M14 PP-1..3) adds
// profitability/rollup queries.
import type { RevenueBasis } from '@pfos/engine';
import {
  profitabilityRollupsResponseSchema,
  projectListResponseSchema,
  projectProfitabilityResponseSchema,
  projectResponseSchema,
  projectValueLensesResponseSchema,
  type ProfitabilityRollupsResponse,
  type ProjectAddAdjustmentRequest,
  type ProjectCreateRequest,
  type ProjectEditRequest,
  type ProjectLogHoursRequest,
  type ProjectProfitabilityResponse,
  type ProjectResponse,
  type ProjectValueLensesResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const PROJECTS_KEY = ['projects'] as const;

// PJ-3 (LOCKED): Final Value = original agreement + Σ adjustments (§14-3) — derivable
// client-side from the list response, no per-row value-lenses fetch needed.
export function computeFinalValueMinor(project: ProjectResponse): number {
  return project.agreedMinor + project.adjustments.reduce((sum, a) => sum + a.amountMinor, 0);
}

export function useProjects(
  filters: { status?: string; partyId?: string } = {},
): UseQueryResult<ProjectResponse[]> {
  const params = new URLSearchParams();
  if (filters.status !== undefined) params.set('status', filters.status);
  if (filters.partyId !== undefined) params.set('partyId', filters.partyId);
  const qs = params.toString();
  return useQuery({
    queryKey: [...PROJECTS_KEY, filters],
    queryFn: async () =>
      projectListResponseSchema.parse(await api.get(`/api/v1/projects${qs === '' ? '' : `?${qs}`}`))
        .projects,
  });
}

export function useProject(id: string): UseQueryResult<ProjectResponse> {
  return useQuery({
    queryKey: [...PROJECTS_KEY, id],
    queryFn: async () => projectResponseSchema.parse(await api.get(`/api/v1/projects/${id}`)),
    enabled: id !== '',
  });
}

export function useProjectValueLenses(id: string): UseQueryResult<ProjectValueLensesResponse> {
  return useQuery({
    queryKey: [...PROJECTS_KEY, id, 'value-lenses'],
    queryFn: async () =>
      projectValueLensesResponseSchema.parse(await api.get(`/api/v1/projects/${id}/value-lenses`)),
    enabled: id !== '',
  });
}

export function useProjectProfitability(
  id: string,
  basis: RevenueBasis = 'final',
): UseQueryResult<ProjectProfitabilityResponse> {
  return useQuery({
    queryKey: [...PROJECTS_KEY, id, 'profitability', basis],
    queryFn: async () =>
      projectProfitabilityResponseSchema.parse(
        await api.get(`/api/v1/projects/${id}/profitability?basis=${basis}`),
      ),
    enabled: id !== '',
  });
}

export function useProfitabilityRollups(
  currency: string,
  basis: RevenueBasis = 'final',
): UseQueryResult<ProfitabilityRollupsResponse> {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'rollups', currency, basis],
    queryFn: async () =>
      profitabilityRollupsResponseSchema.parse(
        await api.get(`/api/v1/projects/rollups?currency=${currency}&basis=${basis}`),
      ),
    enabled: currency !== '',
  });
}

function useInvalidateProjects(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
  };
}

export function useCreateProject(): UseMutationResult<
  ProjectResponse,
  Error,
  ProjectCreateRequest
> {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async (input) =>
      projectResponseSchema.parse(await api.post('/api/v1/projects', input)),
    onSuccess: invalidate,
  });
}

export function useEditProject(
  id: string,
): UseMutationResult<ProjectResponse, Error, ProjectEditRequest> {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async (input) =>
      projectResponseSchema.parse(await api.patch(`/api/v1/projects/${id}`, input)),
    onSuccess: invalidate,
  });
}

export function useAddProjectAdjustment(
  id: string,
): UseMutationResult<ProjectResponse, Error, ProjectAddAdjustmentRequest> {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async (input) =>
      projectResponseSchema.parse(await api.post(`/api/v1/projects/${id}/adjustments`, input)),
    onSuccess: invalidate,
  });
}

export function useLogProjectHours(
  id: string,
): UseMutationResult<ProjectResponse, Error, ProjectLogHoursRequest> {
  const invalidate = useInvalidateProjects();
  return useMutation({
    mutationFn: async (input) =>
      projectResponseSchema.parse(await api.post(`/api/v1/projects/${id}/hours`, input)),
    onSuccess: invalidate,
  });
}
